/**
 * Cross-app client that calls the Mail BFF's Tasks API from inside the
 * Connect web. Used by the "Convert to task" message action so a chat
 * message can become a tracked Mail task without leaving Connect.
 *
 * The Mail BFF's CORS allow-list now includes meeting.fowlguard.com +
 * localhost:5200 (Connect dev port) so this works as a vanilla CORS
 * bearer call. The OIDC token already carries the `fg.mail`
 * scope when issued for the Connect SPA.
 */
const mailBaseUrl = import.meta.env.VITE_MAIL_BFF_BASE_URL ?? "http://localhost:5102";
const mailWebUrl = import.meta.env.VITE_MAIL_WEB_BASE_URL ?? "http://localhost:5180";

export interface MailTaskCreated {
    id: string;
    title: string;
    notes: string | null;
    isCompleted: boolean;
    relatedMessageId: string | null;
}

export const mailTasksApi = {
    /**
     * Create a Mail task from a Connect message. Title is derived from
     * the body excerpt (caller passes the cleaned plain-text); notes
     * carries a back-link to the channel + message id so the user can
     * jump back to the source from the Mail Tasks view.
     */
    createFromChat: async (
        params: {
            title: string;
            notes?: string;
            relatedMessageId?: string;
        },
        token: string | undefined
    ): Promise<MailTaskCreated> => {
        const headers = new Headers({ "Content-Type": "application/json" });
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(`${mailBaseUrl}/api/tasks`, {
            method: "POST",
            headers,
            body: JSON.stringify({
                title: params.title,
                notes: params.notes,
                relatedMessageId: params.relatedMessageId
            }),
            credentials: "omit"
        });
        if (!res.ok) {
            throw new Error(`Mail BFF /api/tasks → ${res.status} ${res.statusText}`);
        }
        return (await res.json()) as MailTaskCreated;
    },

    /** Browser-friendly link the user can click to land in the Mail Tasks page. */
    tasksPageUrl: (): string => `${mailWebUrl}/tasks`
};
